import { Resource, Region, ResourceLocation } from "./types";

type LocationMapping = { resources: Resource[]; disabled: boolean; restriction: string };

type AllLocationsMapping = Record<string, Partial<LocationMapping>>;
class LocationMappingCache<T> {
  private cache: { [cacheKey: string]: T[] } = {};

  get(mapping: AllLocationsMapping): T[] | undefined {
    const cacheKey = JSON.stringify(mapping);

    return this.cache[cacheKey];
  }

  set(mapping: AllLocationsMapping, result: T[]) {
    const cacheKey = JSON.stringify(mapping);

    this.cache[cacheKey] = result;

    return result;
  }
}

const regionsCache = new LocationMappingCache<Region>();
const resourceLocationsCache = new LocationMappingCache<ResourceLocation>();
const stagingResourceLocationsCache = new LocationMappingCache<ResourceLocation>();

const deepFreeze = <T extends {}>(obj: T): T => {
  Object.freeze(obj);

  for (const value of Object.values(obj)) {
    if (typeof value === "object") {
      deepFreeze(value as any);
    }
  }

  return obj;
};

const normalizeLocationMapping = (loc: Partial<LocationMapping> = {}, defaults: LocationMapping) => {
  const { resources, disabled, restriction } = { ...defaults, ...loc };

  return {
    resources: resources.filter((res) => res.count > 0),
    disabled,
    restriction,
  };
};

const defaultRegionMapping: LocationMapping = {
  resources: [],
  disabled: false,
  restriction: "Creates in this datacenter region are disabled",
};

const defaultDatacenterMapping: LocationMapping = {
  resources: [],
  disabled: false,
  restriction: "Creates in this datacenter are disabled",
};

const normalizeRegionMapping = (loc: Partial<LocationMapping> = {}) => normalizeLocationMapping(loc, defaultRegionMapping);
const normalizeDatacenterMapping = (loc: Partial<LocationMapping> = {}) => normalizeLocationMapping(loc, defaultDatacenterMapping);

const noMapping: AllLocationsMapping = {};

const allRegions = (mapping: AllLocationsMapping = noMapping): Region[] => {
  let list = regionsCache.get(mapping);

  if (list) {
    return list;
  }

  list = deepFreeze<Region[]>([
    {
      label: "New York",
      type: "region",
      value: "nyc",
      flag: "usa",
      continent: "North America",
      ...normalizeRegionMapping(mapping.nyc),
    },
    {
      type: "region",
      label: "Amsterdam",
      value: "ams",
      flag: "netherlands",
      continent: "Europe",
      ...normalizeRegionMapping(mapping.ams),
    },
    {
      type: "region",
      label: "San Francisco",
      value: "sfo",
      flag: "usa",
      continent: "North America",
      ...normalizeRegionMapping(mapping.sfo),
    },
    {
      type: "region",
      label: "Singapore",
      value: "sgp",
      flag: "singapore",
      continent: "Asia",
      ...normalizeRegionMapping(mapping.sgp),
    },
    {
      type: "region",
      label: "London",
      value: "lon",
      flag: "uk",
      continent: "Europe",
      ...normalizeRegionMapping(mapping.lon),
    },
    {
      type: "region",
      label: "Frankfurt",
      value: "fra",
      flag: "germany",
      continent: "Europe",
      ...normalizeRegionMapping(mapping.fra),
    },
    {
      type: "region",
      label: "Toronto",
      value: "tor",
      flag: "canada",
      continent: "North America",
      ...normalizeRegionMapping(mapping.tor),
    },
    {
      type: "region",
      label: "Bangalore",
      value: "blr",
      flag: "india",
      continent: "Asia",
      ...normalizeRegionMapping(mapping.blr),
    },
    {
      type: "region",
      label: "Sydney",
      value: "syd",
      flag: "australia",
      continent: "Australia",
      ...normalizeRegionMapping(mapping.syd),
    },
    {
      type: "region",
      label: "Atlanta",
      value: "atl",
      flag: "usa",
      continent: "North America",
      ...normalizeRegionMapping(mapping.atl),
    },
  ]);

  regionsCache.set(mapping, list);

  return list;
};

const allStage2Regions = (mapping: AllLocationsMapping = noMapping): Region[] => {
  let list = regionsCache.get(mapping);
  if (list) {
    return list;
  }
  list = deepFreeze<Region[]>([
    {
      label: "Stage2",
      type: "region",
      value: "s2r",
      flag: "usa",
      continent: "North America",
      ...normalizeRegionMapping(mapping.s2r),
    },
  ]);
  regionsCache.set(mapping, list);
  return list;
};

const allDatacenters = (mapping: AllLocationsMapping = noMapping): ResourceLocation[] => {
  let list = resourceLocationsCache.get(mapping);

  if (list) {
    return list;
  }

  list = deepFreeze<ResourceLocation[]>([
    {
      type: "datacenter",
      label: "New York",
      value: "nyc1",
      datacenterId: "1",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.nyc1),
    },
    {
      type: "datacenter",
      label: "New York",
      value: "nyc2",
      datacenterId: "2",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.nyc2),
    },
    {
      type: "datacenter",
      label: "New York",
      value: "nyc3",
      datacenterId: "3",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.nyc3),
    },
    {
      type: "datacenter",
      label: "San Francisco",
      value: "sfo1",
      datacenterId: "1",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.sfo1),
    },
    {
      type: "datacenter",
      label: "San Francisco",
      value: "sfo2",
      datacenterId: "2",
      flag: "usa",
      continent: "North America",

      ...normalizeDatacenterMapping(mapping.sfousa),
    },
    {
      type: "datacenter",
      label: "San Francisco",
      value: "sfo3",
      datacenterId: "3",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.sfo3),
    },
    {
      type: "datacenter",
      label: "Atlanta",
      value: "atl1",
      datacenterId: "1",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.atl1),
    },
    {
      type: "datacenter",
      label: "Toronto",
      value: "tor1",
      datacenterId: "1",
      flag: "canada",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.tor1),
    },
    {
      type: "datacenter",
      label: "London",
      value: "lon1",
      datacenterId: "1",
      flag: "uk",
      continent: "Europe",
      ...normalizeDatacenterMapping(mapping.lon1),
    },
    {
      type: "datacenter",
      label: "Amsterdam",
      value: "ams2",
      datacenterId: "2",
      flag: "netherlands",
      continent: "Europe",
      ...normalizeDatacenterMapping(mapping.ams2),
    },
    {
      type: "datacenter",
      label: "Amsterdam",
      value: "ams3",
      datacenterId: "3",
      flag: "netherlands",
      continent: "Europe",
      ...normalizeDatacenterMapping(mapping.ams3),
    },
    {
      type: "datacenter",
      label: "Frankfurt",
      value: "fra1",
      datacenterId: "1",
      flag: "germany",
      continent: "Europe",
      ...normalizeDatacenterMapping(mapping.fra1),
    },
    {
      type: "datacenter",
      label: "Singapore",
      value: "sgp1",
      datacenterId: "1",
      flag: "singapore",
      continent: "Asia",
      ...normalizeDatacenterMapping(mapping.sgp1),
    },
    {
      type: "datacenter",
      label: "Bangalore",
      value: "blr1",
      datacenterId: "1",
      flag: "india",
      continent: "Asia",
      ...normalizeDatacenterMapping(mapping.blr1),
    },
    {
      type: "datacenter",
      label: "Sydney",
      value: "syd1",
      datacenterId: "1",
      flag: "australia",
      continent: "Australia",
      ...normalizeDatacenterMapping(mapping.syd1),
    },
  ]);

  resourceLocationsCache.set(mapping, list);

  return list;
};

const allStage2Datacenters = (mapping: AllLocationsMapping = noMapping): ResourceLocation[] => {
  let list = stagingResourceLocationsCache.get(mapping);

  if (list) {
    return list;
  }

  list = deepFreeze<ResourceLocation[]>([
    {
      type: "datacenter",
      label: "Stage2",
      value: "s2r1",
      datacenterId: "1",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.s2r1),
    },
    {
      type: "datacenter",
      label: "Stage2",
      value: "s2r2",
      datacenterId: "2",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.s2r2),
    },
    {
      type: "datacenter",
      label: "Stage2",
      value: "s2r3",
      datacenterId: "3",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.s2r3),
    },
    {
      type: "datacenter",
      label: "Stage2",
      value: "s2r4",
      datacenterId: "4",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.s2r4),
    },
    {
      type: "datacenter",
      label: "Stage2",
      value: "s2r5",
      datacenterId: "5",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.s2r5),
    },
    {
      type: "datacenter",
      label: "Stage2",
      value: "s2r6",
      datacenterId: "6",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.s2r6),
    },
    {
      type: "datacenter",
      label: "Stage2",
      value: "s2r7",
      datacenterId: "7",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.s2r7),
    },
    {
      type: "datacenter",
      label: "Stage2",
      value: "s2r8",
      datacenterId: "8",
      flag: "usa",
      continent: "North America",
      ...normalizeDatacenterMapping(mapping.s2r8),
    },
  ]);

  stagingResourceLocationsCache.set(mapping, list);

  return list;
};

export const locationHelpers = {
  allRegions,
  allStage2Regions,
  allDatacenters,
  allStage2Datacenters,
};

export type LocationHelpers = typeof locationHelpers;
